import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type JSX } from 'react';
import { ApiError } from '@invite/app-core';
import { cancelStay, changeStay, deleteBlock } from '../api/ownerApi';
import { ownerKeys } from '../api/queryKeys';
import type { CalendarBooking, LockedPeriod, OwnerCalendar } from '../api/schemas';
import { isTypeFull, signOutAfterMessage } from '../booking/apiErrors';
import { TYPE_FULL } from '../booking/bookingForm';
import { canChangeStay, validateChange } from '../booking/changeStay';
import { Field } from '../booking/FieldError';
import { useLocale, useT } from '../i18n/useT';
import { formatLongDate } from './format';
import { nights, type IsoDate } from './monthGrid';

interface BookingDetailSheetProps {
  booking: CalendarBooking;
  unitId: number;
  today: IsoDate;
  /** The calendar's bookings and locked nights, to check new dates for a stay. */
  bookings: CalendarBooking[];
  locked: LockedPeriod[];
  /** Called once a block is deleted, or a stay cancelled or moved, to close the sheet. */
  onDeleted: () => void;
}

/** The server's message for a refusal it explains; otherwise a generic one. */
const failureOf = (error: Error | null, fallback: string): string | null =>
  error === null
    ? null
    : error instanceof ApiError &&
        ((error.status >= 400 && error.status < 500) || error.status === 502) &&
        error.message !== ''
      ? error.message
      : fallback;

/**
 * Body of the details sheet. The name and comment are the owner's own; rentals show neither.
 * A block shows its comment to every owner of the unit, and can be deleted until it has ended.
 * An own stay that has not started can be moved or cancelled.
 */
export const BookingDetailSheet = ({
  booking,
  unitId,
  today,
  bookings,
  locked,
  onDeleted
}: BookingDetailSheetProps): JSX.Element => {
  const t = useT();
  const locale = useLocale();
  const count = nights(booking.from, booking.to);
  const status = {
    booked: t('Booked'),
    checked_in: t('Checked in'),
    checked_out: t('Checked out')
  }[booking.status];
  const isOwn = booking.type === 'own';
  const isBlock = booking.type === 'block';
  const typeName = { own: t('Own use'), rental: t('Rental'), block: t('Maintenance') }[
    booking.type
  ];
  return (
    <>
      <dl className="ow-details">
        <dt>{t('Type')}</dt>
        <dd>{typeName}</dd>
        <dt>{t('Dates')}</dt>
        <dd>
          {formatLongDate(booking.from, locale)} – {formatLongDate(booking.to, locale)}
        </dd>
        <dt>{t('Nights')}</dt>
        <dd>{count === 1 ? t('1 night') : t('{0} nights', { 0: count })}</dd>
        {!isBlock && (
          <>
            <dt>{t('Status')}</dt>
            <dd>{status}</dd>
          </>
        )}
        {isOwn && booking.usedBy ? (
          <>
            <dt>{t('Who is staying')}</dt>
            <dd>{booking.usedBy}</dd>
          </>
        ) : null}
        {(isOwn || isBlock) && booking.comment ? (
          <>
            <dt>{t('Comment')}</dt>
            <dd>{booking.comment}</dd>
          </>
        ) : null}
      </dl>
      {isBlock && booking.to > today && (
        <DeleteBlock unitId={unitId} periodId={booking.id} onDeleted={onDeleted} />
      )}
      {canChangeStay(booking, today) && (
        <StayActions
          stay={booking}
          unitId={unitId}
          today={today}
          bookings={bookings}
          locked={locked}
          onDone={onDeleted}
        />
      )}
    </>
  );
};

const DeleteBlock = ({
  unitId,
  periodId,
  onDeleted
}: {
  unitId: number;
  periodId: number;
  onDeleted: () => void;
}): JSX.Element => {
  const t = useT();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const forget = (): void => {
    queryClient.setQueriesData<OwnerCalendar>({ queryKey: ownerKeys.calendarOf(unitId) }, (old) =>
      old === undefined
        ? old
        : {
            ...old,
            bookings: old.bookings.filter((b) => !(b.type === 'block' && b.id === periodId))
          }
    );
    void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unitId) });
    onDeleted();
  };
  const remove = useMutation({
    mutationFn: () => deleteBlock(unitId, periodId),
    onSuccess: forget,
    onError: (error) => {
      // Already gone, e.g. deleted by another owner of the unit: the calendar was out of date.
      if (error instanceof ApiError && error.status === 404) {
        forget();
      }
    }
  });

  const failure = failureOf(remove.error, t('Something went wrong. Please try again.'));

  if (!confirming) {
    return (
      <div className="ow-step-actions">
        <button
          type="button"
          className="ow-btn-secondary"
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t('Delete block')}
        </button>
      </div>
    );
  }
  return (
    <div className="ow-form" role="group" aria-label={t('Delete block')}>
      <p>{t('Delete this block? It is removed from the booking system too.')}</p>
      {failure !== null && (
        <p className="ow-error" role="alert">
          {failure}
        </p>
      )}
      <div className="ow-step-actions">
        <button
          type="button"
          className="ow-btn-secondary"
          disabled={remove.isPending}
          onClick={() => {
            remove.reset();
            setConfirming(false);
          }}
        >
          {t('Cancel')}
        </button>
        <button
          type="button"
          className="ow-btn-primary"
          disabled={remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          {remove.isPending ? t('Deleting…') : t('Delete')}
        </button>
      </div>
    </div>
  );
};

const StayActions = ({
  stay,
  unitId,
  today,
  bookings,
  locked,
  onDone
}: {
  stay: CalendarBooking;
  unitId: number;
  today: IsoDate;
  bookings: CalendarBooking[];
  locked: LockedPeriod[];
  onDone: () => void;
}): JSX.Element => {
  const t = useT();
  const [mode, setMode] = useState<'idle' | 'cancel' | 'change'>('idle');
  const close = (): void => {
    setMode('idle');
  };
  if (mode === 'cancel') {
    return <CancelStay stayId={stay.id} unitId={unitId} onBack={close} onDone={onDone} />;
  }
  if (mode === 'change') {
    return (
      <ChangeStay
        stay={stay}
        unitId={unitId}
        today={today}
        bookings={bookings}
        locked={locked}
        onBack={close}
        onDone={onDone}
      />
    );
  }
  return (
    <div className="ow-step-actions">
      <button
        type="button"
        className="ow-btn-secondary"
        onClick={() => {
          setMode('cancel');
        }}
      >
        {t('Cancel stay')}
      </button>
      <button
        type="button"
        className="ow-btn-primary"
        onClick={() => {
          setMode('change');
        }}
      >
        {t('Change')}
      </button>
    </div>
  );
};

const CancelStay = ({
  stayId,
  unitId,
  onBack,
  onDone
}: {
  stayId: number;
  unitId: number;
  onBack: () => void;
  onDone: () => void;
}): JSX.Element => {
  const t = useT();
  const queryClient = useQueryClient();
  const forget = (): void => {
    queryClient.setQueriesData<OwnerCalendar>({ queryKey: ownerKeys.calendarOf(unitId) }, (old) =>
      old === undefined
        ? old
        : {
            ...old,
            bookings: old.bookings.filter((b) => !(b.type !== 'block' && b.id === stayId))
          }
    );
    void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unitId) });
    onDone();
  };
  const cancel = useMutation({
    mutationFn: () => cancelStay(unitId, stayId),
    onSuccess: forget,
    onError: (error) => {
      if (error instanceof ApiError && error.status === 404) {
        // Already gone, e.g. cancelled at reception: the calendar was out of date.
        forget();
      } else if (!(error instanceof ApiError) || error.status >= 500) {
        // Unconfirmed: the cancel may have landed. Show the owner what the server has.
        void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unitId) });
      }
    }
  });
  const failure = failureOf(cancel.error, t('Something went wrong. Please try again.'));
  return (
    <div className="ow-form" role="group" aria-label={t('Cancel stay')}>
      <p>{t('Cancel this stay? It is cancelled in the booking system too.')}</p>
      {failure !== null && (
        <p className="ow-error" role="alert">
          {failure}
        </p>
      )}
      <div className="ow-step-actions">
        <button
          type="button"
          className="ow-btn-secondary"
          disabled={cancel.isPending}
          onClick={() => {
            cancel.reset();
            onBack();
          }}
        >
          {t('Keep')}
        </button>
        <button
          type="button"
          className="ow-btn-primary"
          disabled={cancel.isPending}
          onClick={() => {
            cancel.mutate();
          }}
        >
          {cancel.isPending ? t('Cancelling…') : t('Cancel stay')}
        </button>
      </div>
    </div>
  );
};

const ChangeStay = ({
  stay,
  unitId,
  today,
  bookings,
  locked,
  onBack,
  onDone
}: {
  stay: CalendarBooking;
  unitId: number;
  today: IsoDate;
  bookings: CalendarBooking[];
  locked: LockedPeriod[];
  onBack: () => void;
  onDone: () => void;
}): JSX.Element => {
  const t = useT();
  const queryClient = useQueryClient();
  const [dates, setDates] = useState({ from: stay.from, to: stay.to });
  const [errors, setErrors] = useState<Record<string, string>>({});
  // One key per attempted pair of dates: a retry of the same dates may be answered from the first.
  const lastAttempt = useRef<{ body: string; key: string } | null>(null);
  const keyFor = (body: string): string => {
    if (lastAttempt.current?.body !== body) {
      lastAttempt.current = { body, key: crypto.randomUUID() };
    }
    return lastAttempt.current.key;
  };
  const change = useMutation({
    mutationFn: () =>
      changeStay(unitId, stay.id, {
        ...dates,
        idempotencyKey: keyFor(JSON.stringify(dates))
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unitId) });
      onDone();
    },
    onError: (error) => {
      if (!(error instanceof ApiError) || error.status >= 500 || isTypeFull(error)) {
        // Unconfirmed, or the copy was stale: show the owner what the server has.
        void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unitId) });
      } else if (error.status === 404) {
        // Already gone, e.g. cancelled at reception.
        void queryClient.invalidateQueries({ queryKey: ownerKeys.calendarOf(unitId) });
        onDone();
      } else if (error.status === 401) {
        signOutAfterMessage();
      }
    }
  });
  const error = change.error;
  const failure =
    error instanceof ApiError && error.status === 401
      ? t('Your session has expired. Please sign in again.')
      : error instanceof ApiError && isTypeFull(error)
        ? t(TYPE_FULL)
        : failureOf(error, t('Something went wrong. Please try again.'));
  const edit = (patch: Partial<typeof dates>): void => {
    setDates((old) => ({ ...old, ...patch }));
    setErrors({});
    change.reset();
  };
  return (
    <form
      className="ow-form"
      aria-label={t('Change dates')}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        const found = validateChange(stay, dates, bookings, locked, today);
        setErrors(found);
        if (Object.keys(found).length === 0) {
          change.mutate();
        }
      }}
    >
      <Field label={t('From')} error={errors['from']}>
        {(a11y) => (
          <input
            {...a11y}
            className="ow-field"
            type="date"
            value={dates.from}
            onChange={(e) => {
              edit({ from: e.target.value });
            }}
          />
        )}
      </Field>
      <Field label={t('To')} error={errors['to']}>
        {(a11y) => (
          <input
            {...a11y}
            className="ow-field"
            type="date"
            value={dates.to}
            onChange={(e) => {
              edit({ to: e.target.value });
            }}
          />
        )}
      </Field>
      {failure !== null && (
        <p className="ow-error" role="alert">
          {failure}
        </p>
      )}
      <div className="ow-step-actions">
        <button
          type="button"
          className="ow-btn-secondary"
          disabled={change.isPending}
          onClick={() => {
            change.reset();
            onBack();
          }}
        >
          {t('Back')}
        </button>
        <button type="submit" className="ow-btn-primary" disabled={change.isPending}>
          {change.isPending ? t('Saving…') : t('Save')}
        </button>
      </div>
    </form>
  );
};
